'use client'

import { DocumentSearchResult } from '@/dc-shared-web/src/hooks/api/document/types'
import { createContext, useContext, useState, ReactNode } from 'react'

export type StepsData = {
  step1: {
    file: File | undefined
  }
  step2: {
    fileName: string | undefined
  }
}

interface ModalContextType {
  currentStep: number
  stepsData: StepsData
  document: DocumentSearchResult
  nextStep: () => void
  prevStep: () => void
  updateStepsData: (
    stepKey: keyof StepsData,
    data: StepsData[keyof StepsData]
  ) => void
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

const initialStepsData: StepsData = {
  step1: {
    file: undefined,
  },
  step2: {
    fileName: undefined,
  },
}

interface UploadVersionProviderProps {
  children: ReactNode
  document: DocumentSearchResult
}

export function UploadVersionProvider({
  children,
  document,
}: UploadVersionProviderProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [stepsData, setStepsData] = useState<StepsData>(initialStepsData)

  const nextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 3))
  }

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1))
  }

  const updateStepsData = (
    stepKey: keyof StepsData,
    data: StepsData[keyof StepsData]
  ) => {
    setStepsData((prev) => ({
      ...prev,
      [stepKey]: data,
    }))
  }

  return (
    <ModalContext.Provider
      value={{
        currentStep,
        stepsData,
        document,
        nextStep,
        prevStep,
        updateStepsData,
      }}
    >
      {children}
    </ModalContext.Provider>
  )
}

export function useUploadVersion() {
  const context = useContext(ModalContext)

  if (context === undefined) {
    throw new Error(
      'useUploadVersion must be used within a UploadVersionProvider'
    )
  }

  return context
}
